import React, { useEffect, useState } from 'react';
import { Activity, RefreshCw, XCircle, Eye } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { RenderJob } from '../types/index.js';

export const Processings: React.FC = () => {
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState<string[] | null>(null);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const res = await api.getJobs();
      setJobs(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleCancel = async (id: string) => {
    if (!confirm('Deseja cancelar forçadamente este job no worker?')) return;
    try {
      await api.cancelJob(id);
      loadJobs();
    } catch (err: any) {
      alert(err.message || 'Erro ao cancelar job.');
    }
  };

  const getStatusBadge = (status: RenderJob['status']) => {
    switch (status) {
      case 'completed':
        return <EduBadge variant="success" dot>Concluído</EduBadge>;
      case 'failed':
        return <EduBadge variant="error" dot>Falhou</EduBadge>;
      case 'cancelled':
        return <EduBadge variant="neutral" dot>Cancelado</EduBadge>;
      case 'queued':
        return <EduBadge variant="warning" dot>Fila</EduBadge>;
      default:
        return <EduBadge variant="orange" dot>Processando</EduBadge>;
    }
  };

  return (
    <div>
      <Header
        title="Fila Global de Processamento"
        subtitle="Monitoramento em tempo real de todas as tarefas de renderização distribuídas pelos workers"
        action={
          <EduButton
            variant="secondary"
            size="sm"
            onClick={loadJobs}
            icon={<RefreshCw size={14} className={loading ? 'animate-spin' : ''} />}
          >
            Atualizar
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <EduCard title="Tarefas de Renderização (Jobs)">
          {loading && jobs.length === 0 ? (
            <div className="p-12 text-center text-xs text-txt-muted">Carregando jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-10 text-xs text-txt-secondary">
              Nenhuma tarefa em execução ou concluída recentemente.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-bdr text-txt-secondary font-semibold bg-surface-secondary">
                    <th className="py-3 px-4">Job ID</th>
                    <th className="py-3 px-4">Cliente (Tenant)</th>
                    <th className="py-3 px-4">Aula ID</th>
                    <th className="py-3 px-4">Worker ID</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Etapa Atual</th>
                    <th className="py-3 px-4">Progresso</th>
                    <th className="py-3 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bdr text-txt-primary">
                  {jobs.map((j) => (
                    <tr key={j.id} className="hover:bg-surface-secondary/40">
                      <td className="py-3 px-4 font-mono font-semibold">#{j.id.substring(0, 8)}</td>
                      <td className="py-3 px-4 font-medium">{j.customerId}</td>
                      <td className="py-3 px-4 font-mono text-txt-secondary">{j.lessonId}</td>
                      <td className="py-3 px-4 font-mono text-[11px] text-txt-muted">
                        {j.workerId || '—'}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(j.status)}</td>
                      <td className="py-3 px-4 max-w-xs truncate text-txt-secondary">
                        {j.currentStep || 'Aguardando'}
                      </td>
                      <td className="py-3 px-4 font-bold font-mono">
                        {j.progress}%
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        {j.logs && j.logs.length > 0 && (
                          <button
                            onClick={() => setSelectedLogs(j.logs || [])}
                            className="p-1 text-txt-secondary hover:text-brand-primary"
                            title="Ver logs detalhados"
                          >
                            <Eye size={15} />
                          </button>
                        )}
                        {!['completed', 'failed', 'cancelled'].includes(j.status) && (
                          <button
                            onClick={() => handleCancel(j.id)}
                            className="p-1 text-txt-muted hover:text-status-error"
                            title="Cancelar tarefa"
                          >
                            <XCircle size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </EduCard>
      </div>

      {/* Logs Modal */}
      {selectedLogs && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-2xl max-w-2xl w-full p-6">
            <h3 className="text-sm font-bold text-txt-primary mb-3">Logs da Tarefa</h3>
            <div className="bg-brand-graphite text-green-400 p-4 rounded-lg font-mono text-xs max-h-80 overflow-y-auto space-y-1">
              {selectedLogs.map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <EduButton variant="secondary" size="sm" onClick={() => setSelectedLogs(null)}>
                Fechar
              </EduButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
